import React, { useState } from 'react';
import { 
  PackageOpen, 
  Plus, 
  Edit2, 
  Trash2, 
  Eye, 
  EyeOff, 
  Archive,
  Clock,
  Sparkles,
  Shirt
} from 'lucide-react';

const SEASONS = [
  { value: 'лето', label: 'Лето' },
  { value: 'зима', label: 'Зима' },
  { value: 'межсезонье', label: 'Межсезонье' },
  { value: 'круглогодичная', label: 'Круглогодичная' },
];

const STATUS_LABELS = {
  draft: { label: 'Черновик', color: 'var(--light-gray)' },
  published: { label: 'Опубликовано', color: 'var(--warm-sage)' },
  archived: { label: 'В архиве', color: 'var(--mid-gray)' }
};

const CapsulesTab = ({ 
  capsules, 
  onAdd, 
  onEdit, 
  onDelete, 
  onPublish, 
  onArchive,
  onViewDetail 
}) => {
  const [statusFilter, setStatusFilter] = useState('all');
  
  const filteredCapsules = statusFilter === 'all' 
    ? capsules 
    : capsules.filter(c => c.status === statusFilter);

  const isRecent = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    return (now - date) < 7 * 24 * 60 * 60 * 1000; // 7 days
  };

  return (
    <div>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 'var(--space-l)',
        flexWrap: 'wrap',
        gap: 'var(--space-m)'
      }}>
        <div style={{ display: 'flex', gap: 'var(--space-s)' }}>
          {['all', 'draft', 'published', 'archived'].map((status) => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              style={{
                padding: '8px 16px',
                backgroundColor: statusFilter === status ? 'var(--black)' : 'var(--white)',
                color: statusFilter === status ? 'var(--white)' : 'var(--charcoal)',
                border: '1px solid var(--soft-gray)',
                fontFamily: 'var(--font-body)',
                fontSize: '0.8125rem',
                cursor: 'pointer'
              }}
            >
              {status === 'all' ? 'Все' : STATUS_LABELS[status].label}
              {status !== 'all' && (
                <span style={{ marginLeft: '6px', opacity: 0.7 }}>
                  ({capsules.filter(c => c.status === status).length})
                </span>
              )}
            </button>
          ))}
        </div>
        
        <button
          onClick={onAdd}
          data-testid="create-capsule-btn"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-s)',
            padding: '10px 20px',
            backgroundColor: 'var(--black)',
            color: 'var(--white)',
            border: 'none',
            fontFamily: 'var(--font-body)',
            fontSize: '0.8125rem',
            cursor: 'pointer'
          }}
        >
          <Plus size={16} />
          Создать капсулу
        </button>
      </div>

      {/* Capsules Grid */}
      {filteredCapsules.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: 'var(--space-xxl)',
          backgroundColor: 'var(--white)',
          border: '1px solid var(--soft-gray)'
        }}>
          <PackageOpen size={48} style={{ color: 'var(--light-gray)', marginBottom: 'var(--space-l)' }} />
          <p style={{ fontFamily: 'var(--font-display)', fontSize: '1.25rem', marginBottom: 'var(--space-m)' }}>
            {statusFilter === 'all' ? 'Нет капсул' : `Нет капсул со статусом "${STATUS_LABELS[statusFilter]?.label}"`}
          </p>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: '0.875rem', color: 'var(--mid-gray)' }}>
            Создайте первую капсулу для этого клиента
          </p>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
          gap: 'var(--space-l)'
        }}>
          {filteredCapsules.map((capsule) => (
            <div
              key={capsule.id}
              data-testid={`capsule-card-${capsule.id}`}
              style={{
                backgroundColor: 'var(--white)',
                border: '1px solid var(--soft-gray)',
                overflow: 'hidden',
                position: 'relative'
              }}
            >
              {/* Cover Image */}
              <div style={{
                height: '160px',
                backgroundColor: 'var(--cream)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                cursor: 'pointer'
              }}
              onClick={() => onViewDetail(capsule)}
              >
                {capsule.cover_image ? (
                  <img 
                    src={capsule.cover_image} 
                    alt={capsule.name}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <PackageOpen size={48} style={{ color: 'var(--light-gray)' }} />
                )}
              </div>

              {/* Status Badge */}
              <div style={{
                position: 'absolute',
                top: '12px',
                left: '12px',
                display: 'flex',
                gap: 'var(--space-s)'
              }}>
                <span style={{
                  padding: '4px 10px',
                  backgroundColor: 'rgba(255,255,255,0.95)',
                  fontSize: '0.6875rem',
                  fontFamily: 'var(--font-body)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  color: STATUS_LABELS[capsule.status]?.color || 'var(--mid-gray)'
                }}>
                  {STATUS_LABELS[capsule.status]?.label || capsule.status}
                </span>
                {isRecent(capsule.updated_at) && (
                  <span style={{
                    padding: '4px 10px',
                    backgroundColor: 'var(--accent-gold)',
                    color: 'var(--black)',
                    fontSize: '0.6875rem',
                    fontFamily: 'var(--font-body)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Обновлено
                  </span>
                )}
              </div>

              {/* Content */}
              <div style={{ padding: 'var(--space-l)' }}>
                <div style={{ marginBottom: 'var(--space-m)' }}>
                  <h4 
                    style={{
                      fontFamily: 'var(--font-display)',
                      fontSize: '1.125rem',
                      marginBottom: 'var(--space-xs)',
                      cursor: 'pointer'
                    }}
                    onClick={() => onViewDetail(capsule)}
                  >
                    {capsule.name}
                  </h4>
                  <p style={{
                    fontFamily: 'var(--font-body)',
                    fontSize: '0.75rem',
                    color: 'var(--light-gray)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    {capsule.season}
                  </p>
                </div>

                {capsule.description && (
                  <p style={{
                    fontFamily: 'var(--font-body)',
                    fontSize: '0.875rem',
                    color: 'var(--mid-gray)',
                    marginBottom: 'var(--space-m)',
                    lineHeight: 1.5,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}>
                    {capsule.description}
                  </p>
                )}

                {/* Event Tags */}
                {capsule.event_types?.length > 0 && (
                  <div style={{
                    display: 'flex',
                    gap: 'var(--space-xs)',
                    flexWrap: 'wrap',
                    marginBottom: 'var(--space-m)'
                  }}>
                    {capsule.event_types.slice(0, 3).map((event, idx) => (
                      <span key={idx} style={{
                        padding: '2px 8px',
                        backgroundColor: 'var(--cream)',
                        fontSize: '0.6875rem',
                        fontFamily: 'var(--font-body)'
                      }}>
                        {event}
                      </span>
                    ))}
                    {capsule.event_types.length > 3 && (
                      <span style={{
                        padding: '2px 8px',
                        backgroundColor: 'var(--cream)',
                        fontSize: '0.6875rem',
                        fontFamily: 'var(--font-body)'
                      }}>
                        +{capsule.event_types.length - 3}
                      </span>
                    )}
                  </div>
                )}

                {/* Stats */}
                <div style={{
                  display: 'flex',
                  gap: 'var(--space-l)',
                  paddingTop: 'var(--space-m)',
                  borderTop: '1px solid var(--soft-gray)',
                  marginBottom: 'var(--space-m)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)' }}>
                    <Shirt size={14} style={{ color: 'var(--light-gray)' }} />
                    <span style={{ fontFamily: 'var(--font-body)', fontSize: '0.875rem' }}>
                      {capsule.items_count} вещей
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)' }}>
                    <Sparkles size={14} style={{ color: 'var(--light-gray)' }} />
                    <span style={{ fontFamily: 'var(--font-body)', fontSize: '0.875rem' }}>
                      {capsule.looks_count} образов
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div style={{
                  display: 'flex',
                  gap: 'var(--space-s)',
                  flexWrap: 'wrap'
                }}>
                  <button
                    onClick={() => onViewDetail(capsule)}
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      backgroundColor: 'var(--black)',
                      color: 'var(--white)',
                      border: 'none',
                      fontFamily: 'var(--font-body)',
                      fontSize: '0.75rem',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 'var(--space-xs)'
                    }}
                  >
                    <Edit2 size={12} />
                    Редактировать
                  </button>
                  
                  {capsule.status === 'draft' && (
                    <button
                      onClick={() => onPublish(capsule.id)}
                      title="Опубликовать"
                      style={{
                        padding: '8px 12px',
                        backgroundColor: 'var(--warm-sage)',
                        color: 'var(--white)',
                        border: 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      <Eye size={14} />
                    </button>
                  )}
                  
                  {capsule.status === 'published' && (
                    <button
                      onClick={() => onArchive(capsule.id)}
                      title="Архивировать"
                      style={{
                        padding: '8px 12px',
                        backgroundColor: 'var(--cream)',
                        border: 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      <Archive size={14} />
                    </button>
                  )}
                  
                  <button
                    onClick={() => onDelete(capsule.id)}
                    title="Удалить"
                    style={{
                      padding: '8px 12px',
                      backgroundColor: 'var(--cream)',
                      border: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      color: '#ef4444'
                    }}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CapsulesTab;
